import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import Banner from '@/components/UI/Banner';
import Button from '@/components/UI/Button';
import { downloadFile } from '@/components/UI/Table/Reusable/APITable/APITable.utils';
import { errorToast, successToast } from '@/helpers';
import useMountedState from '@/support/Hooks/fetch/useMountedState';
import { useModalAction } from '@/utils/modal';
import { useDataTable } from '@/components/UI/DataTableV2/DataTableProvider';
import { useScrollSync } from '@/components/UI/DataTableV2/hooks/useScrollSync';
import { useAutoScroll } from '@/components/UI/DataTableV2/hooks/useAutoScroll';
import { useDataTableErrors } from '@/components/UI/DataTableV2/hooks/useDataTableErrors';
import { useColumns } from '@/components/UI/DataTableV2/hooks/useColumns';
import { useRows } from '@/components/UI/DataTableV2/hooks/useRows';
import { ScrollableGroupHeaders } from '@/components/UI/DataTableV2/headers/ScrollableGroupHeaders';
import { ScrollableColumnHeaders } from '@/components/UI/DataTableV2/headers/ScrollableColumnHeaders';
import { StickyPinnedColumnHeaders } from '@/components/UI/DataTableV2/headers/StickyPinnedColumnHeaders';
import {
  DataTableRowColumnHeaders,
  DataTableRowHeaderStickyContainer,
} from '@/components/UI/DataTableV2/headers/DataTableRowHeader';
import { StickyRowHeaders } from '@/components/UI/DataTableV2/headers/StickyRowHeaders';
import { StickyPinnedCells } from '@/components/UI/DataTableV2/cells/StickyPinnedCells';
import { VirtualizedCells } from '@/components/UI/DataTableV2/cells/VirtualizedCells';
import { DataTableToolbar } from '@/components/UI/DataTableV2/toolbar/DataTableToolbar';
import { DataTableOnlineUsers } from '@/components/UI/DataTableV2/DataTableOnlineUsers/DataTableOnlineUsers';
import { RemoteSelectionInteractionProvider } from '@/components/UI/DataTableV2/RemoteSelectionInteractionContext';
import { RemoteSelectionsOverlay } from '@/components/UI/DataTableV2/RemoteSelectionsOverlay/RemoteSelectionsOverlay';
import type {
  DataTableHighVolumeUpsertEvent,
  DataTableRowHeaderStateChangeEvent,
} from '@/components/UI/DataTableV2/DataTable.model';
import { DataTableEvent } from '@/components/UI/DataTableV2/DataTable.model';
import styles from './DataTableV2.module.scss';
import { _notNil } from '@/littledash';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';
import { useSidebarAutoCollapse } from '@/components/UI/DataTableV2/hooks/useSidebarAutoCollapse';

const GROUP_HEADER_ROW_HEIGHT = 40;

export const DataTableView: FC = () => {
  const { dataTableService } = useDataTable();
  const { id: studyId, dataTableId } = useParams<{ id: string; dataTableId: string }>();
  const isMounted = useMountedState();
  const { openModal, closeModal } = useModalAction();

  useSidebarAutoCollapse();

  const scrollElementRef = useRef<HTMLDivElement>(null);
  const columnHeadersRef = useRef<HTMLDivElement>(null);
  const rowHeadersRef = useRef<HTMLDivElement>(null);

  const errors = useDataTableErrors();
  const leafColumns = useColumns('leaf');
  const rows = useRows();
  const [rowHeaderWidth, setRowHeaderWidth] = useState(dataTableService.rowHeaderWidth());

  const dimensions = dataTableService.dimensions;
  const columnGroupingData = dataTableService.getColumnGroupingData();

  const hasGroupedColumns = columnGroupingData !== null && columnGroupingData.columnGroups.length > 0;

  const { pinnedDataColumns, scrollableColumns, pinnedColumnWidth, scrollableColumnCount } = useMemo(() => {
    const pinnedDataColumnIds = new Set(dataTableService.getPinnedColumns().map((p) => p.id));
    const pinnedDataColumns = leafColumns.filter((col) => pinnedDataColumnIds.has(col.id));
    const scrollableColumns = leafColumns.filter((col) => !pinnedDataColumnIds.has(col.id));

    const pinnedColumnWidth = pinnedDataColumns.length * dimensions.cellWidth;
    const scrollableColumnCount = scrollableColumns.length;

    return {
      pinnedDataColumns,
      scrollableColumns,
      pinnedColumnWidth,
      scrollableColumnCount,
    };
  }, [leafColumns, dimensions.cellWidth]);

  const { invoke: exportDatatable, loading: exportingDataTable } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'datatables.export',
    path: { studyId, dataTableId },
    options: {
      requestConfigOverrides: { responseType: 'blob' },
      invokeOnInit: false,
      onError: { slug: 'datatable-export', toast: false, throw: false, capture: true },
    },
  });

  useEffect(() => {
    const abortController = new AbortController();
    const rowHeaderStateChangeListener = (event: DataTableRowHeaderStateChangeEvent) => {
      setRowHeaderWidth(event.detail.rowHeaderWidth);
    };

    const pasteLimitExceededListener = (): void => {
      try {
        openModal('INFO', {
          text: 'A maximum of 20000 cells can be pasted.',
          btnTxt: 'OK',
        });
      } catch (error) {
        ExceptionHandler.captureException(
          new InVivoError('Paste limit exceeded handler failed', {
            cause: error,
            slug: 'datatable-view-event-handler',
            level: 'error',
            context: {
              component: 'DataTableView',
              eventType: 'PasteLimitExceeded',
            },
          })
        );
      }
    };

    const highVolumeUpsertListener = (event: DataTableHighVolumeUpsertEvent): void => {
      try {
        if (dataTableService.readonly) {
          return;
        }

        const { totalChunkedRequests, pending, rejected } = event.detail;

        if (rejected.length > 0) {
          if (dataTableService.undoEnabled()) {
            dataTableService.undo();
            closeModal();
            errorToast(
              'There was a problem uploading your data. We have reverted the last change made. Please try again.'
            );
          } else {
            errorToast(
              'A problem occurred while uploading data, and the changes could not be automatically reverted. Please review your data.'
            );
          }
          return;
        }

        if (totalChunkedRequests === pending.length && _notNil(dataTableService)) {
          openModal('DT_HIGH_VOLUME_UPSERT', { dataTableService });
        }
      } catch (error) {
        ExceptionHandler.captureException(
          new InVivoError('High volume upsert handler failed', {
            cause: error,
            slug: 'datatable-view-event-handler',
            level: 'error',
            context: {
              component: 'DataTableView',
              eventType: 'HighVolumeUpsert',
              totalChunkedRequests: String(event.detail.totalChunkedRequests),
              pendingCount: String(event.detail.pending.length),
              rejectedCount: String(event.detail.rejected.length),
            },
          })
        );
      }
    };

    dataTableService.subscribe(
      DataTableEvent.RowHeaderStateChange,
      rowHeaderStateChangeListener,
      abortController.signal
    );
    dataTableService.subscribe(DataTableEvent.PasteLimitExceeded, pasteLimitExceededListener, abortController.signal);
    dataTableService.subscribe(DataTableEvent.HighVolumeUpsert, highVolumeUpsertListener, abortController.signal);
    return () => {
      abortController.abort('teardown');
    };
  }, [dataTableService, openModal, closeModal]);

  const handleExportDataTable = async (): Promise<void> => {
    const response = await exportDatatable({ path: { studyId, dataTableId } });

    if (!isMounted()) {
      return;
    }

    if (response.type === 'success') {
      downloadFile({ data: response.body, headers: response.headers });
      successToast(`Downloaded ${response.headers['file-name']}`);
    } else {
      errorToast('Could not export datatable. Please try again later.');
    }
  };

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: () => dimensions.cellHeight,
    overscan: 10,
  });

  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: scrollableColumnCount,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: () => dimensions.cellWidth,
    overscan: 5,
  });

  useScrollSync({
    scrollElementRef,
    horizontalTargetRef: columnHeadersRef,
    verticalTargetRef: rowHeadersRef,
    rowVirtualizer,
    columnVirtualizer,
    dataTableService,
  });

  useAutoScroll({
    rowVirtualizer,
    columnVirtualizer,
    dataTableService,
    pinnedColumnCount: pinnedDataColumns.length,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const virtualColumns = columnVirtualizer.getVirtualItems();

  const stickyWidth = rowHeaderWidth + pinnedColumnWidth;
  const groupHeaderHeight = hasGroupedColumns ? GROUP_HEADER_ROW_HEIGHT : 0;
  const totalHeaderHeight = groupHeaderHeight + dimensions.columnHeight;

  return (
    <RemoteSelectionInteractionProvider rowVirtualizer={rowVirtualizer} pinnedColumnCount={pinnedDataColumns.length}>
      <div className="h-100 flex flex-column">
        <div className="flex-shrink-0 flex justify-between items-center ph3 pv2 bb b--moon-gray bg-white">
          <h3 className="f6 lh-title near-black fw5">{dataTableService.name}</h3>
          <div className="flex items-center">
            <DataTableOnlineUsers />
            <Button onClick={handleExportDataTable} disabled={exportingDataTable}>
              Export
            </Button>
          </div>
        </div>

        {dataTableService.readonly && (
          <Banner className="flex-shrink-0 mw6 ma3" info>
            <h3 className="f5 lh-title">You don&apos;t have access to edit this Datatable.</h3>
            <p className="f6 lh-title measure mt2">Request &apos;Write&apos; access from the Study author or owner.</p>
          </Banner>
        )}

        <div className="flex-shrink-0 bg-white">
          <DataTableToolbar />
        </div>

        <div className={styles.gridContainer}>
          <div className={styles.headerRow}>
            <div
              className={styles.stickyHeader}
              style={{
                width: `${stickyWidth}px`,
                height: `${totalHeaderHeight}px`,
              }}
            >
              <div className={styles.virtualContainer}>
                <DataTableRowColumnHeaders groupHeaderHeight={groupHeaderHeight} />
                <StickyPinnedColumnHeaders
                  pinnedDataColumns={pinnedDataColumns}
                  rowHeaderWidth={rowHeaderWidth}
                  groupHeaderHeight={groupHeaderHeight}
                  dimensions={dimensions}
                  columnGroupingData={columnGroupingData}
                />
              </div>
              <div className={styles.zoneBorderRight} />
              <div className={styles.zoneBorderBottom} />
            </div>

            <div
              className={styles.scrollableHeader}
              style={{
                height: `${totalHeaderHeight}px`,
              }}
            >
              <div
                ref={columnHeadersRef}
                className={styles.virtualContainer}
                style={{
                  width: `${columnVirtualizer.getTotalSize()}px`,
                  height: `${totalHeaderHeight}px`,
                }}
              >
                {hasGroupedColumns && _notNil(columnGroupingData) && (
                  <ScrollableGroupHeaders
                    columnGroupingData={columnGroupingData}
                    virtualColumns={virtualColumns}
                    stickyColumnCount={0}
                    dimensions={dimensions}
                    scrollElementRef={scrollElementRef}
                    pinnedColumnWidth={pinnedColumnWidth}
                  />
                )}
                <ScrollableColumnHeaders
                  virtualColumns={virtualColumns}
                  scrollableColumns={scrollableColumns}
                  stickyColumnCount={0}
                  groupHeaderHeight={groupHeaderHeight}
                  dimensions={dimensions}
                  columnGroupingData={columnGroupingData}
                />
              </div>
              <div className={styles.zoneBorderBottom} />
            </div>
          </div>

          <div className={styles.bodyRow}>
            <div
              className={styles.stickyRows}
              style={{
                width: `${stickyWidth}px`,
              }}
            >
              <div
                ref={rowHeadersRef}
                className={styles.virtualContainer}
                style={{
                  width: `${stickyWidth}px`,
                  height: `${rowVirtualizer.getTotalSize()}px`,
                }}
              >
                <DataTableRowHeaderStickyContainer rowHeaderWidth={rowHeaderWidth}>
                  <StickyRowHeaders
                    virtualRows={virtualRows}
                    rowHeaderWidth={rowHeaderWidth}
                    dimensions={dimensions}
                    errors={errors}
                  />
                </DataTableRowHeaderStickyContainer>
                <StickyPinnedCells
                  virtualRows={virtualRows}
                  pinnedDataColumns={pinnedDataColumns}
                  rowHeaderWidth={rowHeaderWidth}
                  dimensions={dimensions}
                />
              </div>
              <div className={styles.zoneBorderRight} />
            </div>

            <div ref={scrollElementRef} className={styles.mainGrid}>
              <div
                className={styles.virtualContainer}
                style={{
                  width: `${columnVirtualizer.getTotalSize()}px`,
                  height: `${rowVirtualizer.getTotalSize()}px`,
                }}
              >
                <VirtualizedCells
                  virtualRows={virtualRows}
                  virtualColumns={virtualColumns}
                  scrollableColumns={scrollableColumns}
                />
                <RemoteSelectionsOverlay />
              </div>
            </div>
          </div>
        </div>
      </div>
    </RemoteSelectionInteractionProvider>
  );
};
